import FilesService from "../services/FilesService";
import { CONTENT_IMAGE_FORMAT } from "@validation/acceptFormats";
import { ReactComponent as AudioPostPlaceholder } from "@assets/ic-audio-placeholder.svg";
import { ReactComponent as TextPostPlaceholder } from "@assets/ic-text-placeholder.svg";
import { getCurrentSrc } from "./posts";
import { imageResizedKey } from "@const/feed";

/**
 * Get image link
 * @param {{ resizedImages?: Record<string, { link: string }>, link?: string }} image
 * @returns string
 */
export const getImageLink = (image) => {
  if (image?.resizedImages) {
    return image?.resizedImages[Object.keys(image?.resizedImages)[0]]?.link;
  }
  return image?.link;
};

/**
 * Get permission to upload files to IPFS
 * @param {Array<{contentType: string, partsCount: number, height?: number, width?: number}>} files
 * @returns array of permissions
 */
export const getUploadPermissions = async (files) => {
  const { data } = await FilesService.requestMultipartUpload(files);

  return data;
};

/**
 * Get image width and height
 * @param {File} file
 */
export const getImageDimensions = async (file) => {
  const { width, height } = await new Promise((resolve, reject) => {
    let img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

  return {
    width,
    height,
  };
};

/**
 * Converts base64 string to File
 * @param {string} dataUrl
 * @param {string} fileName
 * @returns {File}
 */
export const dataUrlToFile = (dataUrl, fileName = "file") => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = window.atob(arr[1]);
  // const bstr = Buffer.from(arr[1], "base64");
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], fileName, { type: mime });
};

/**
 * Returns media type w/o filename extension
 * @param {strings} type
 * @returns media type, e.g.: `image` or `video` or `audio`
 */
export const getMediaType = (type) => type?.split("/")?.[0];

/**
 * Check if file is image or not
 * @param {File} file
 * @returns `true` for image files, `false` for any other.
 */
export const isImageFile = (file) =>
  file?.type?.includes?.("image") || file?.contentType?.includes("image");

/**
 * Check if file is video or not
 * @param {File} file
 * @returns `true` for video files, `false` for any other.
 */
export const isVideoFile = (file) =>
  file?.type?.includes?.("video") || file?.contentType?.includes("video");

/**
 * Check if file is audio or not
 * @param {File} file
 * @returns `true` for video files, `false` for any other.
 */
export const isAudioFile = (file) =>
  file?.type?.includes?.("audio") || file?.contentType?.includes("audio");

/**
 * Check if file is audio or video
 * @param {File} file
 * @returns `true` for audio/video files, `false` for any other.
 */
export const isMediaFile = (file) => isVideoFile(file) || isAudioFile(file);

/**
 * Sort files by format (images always should be first)
 * @param {File[]} files
 * @returns {File[]}
 */
export const sortFilesByFormat = (files) =>
  files.sort((a, b) => {
    const fileExtA = a?.file?.path?.split(".")?.pop()?.toLowerCase();
    const fileExtB = b?.file?.path?.split(".")?.pop()?.toLowerCase();
    if (
      CONTENT_IMAGE_FORMAT.toLowerCase().includes(fileExtB) &&
      !CONTENT_IMAGE_FORMAT.toLowerCase().includes(fileExtA)
    ) {
      return 1;
    }
    if (
      !CONTENT_IMAGE_FORMAT.includes(fileExtB) &&
      CONTENT_IMAGE_FORMAT.includes(fileExtA)
    ) {
      return -1;
    }
    return 0;
  });

export const getPostPlaceholder = (file, audiosCount) => {
  const isAudio = isAudioFile(file);

  if ((!file && audiosCount > 0) || isAudio) {
    return AudioPostPlaceholder;
  }
  return TextPostPlaceholder;
};

export const getPreviewImage = ({ previewImage, file }) => {
  const tempFile = previewImage || file;
  const image = getCurrentSrc(tempFile);

  return isImageFile(tempFile) ? image : undefined;
};

export const getMaxFileHeight = (files) => {
  return (
    Math.max(
      ...files.map(({ file, previewImage }) => {
        const height =
          previewImage?.resizedImages?.[imageResizedKey]?.height ||
          previewImage?.height ||
          file?.resizedImages?.[imageResizedKey]?.height ||
          file?.height;
        return height && !isNaN(height) ? height : 0;
      }),
    ) || "100%"
  );
};
